import { useMemo } from 'react';
import { Select } from './Select';
import { useCountries } from '../../providers/ReferenceDataProvider';
import { isPreferredCountry } from '../../providers/preferred-reference-data';
import type { GlobalSize } from '../../types';
import type { TestIdProps } from '../../utils';

export interface CountrySelectorProps extends TestIdProps {
  id?: string;
  /** ISO 3166-1 alpha-2 country code (case-insensitive); empty/null clears the selection. */
  value: string | null | undefined;
  /** Called with the uppercased ISO code (or `''` when the unselected option is picked). */
  onChange: (code: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Open the dropdown on mount — useful when used as an inline editor. */
  defaultOpen?: boolean;
  /** @default true */
  isSearchable?: boolean;
  height?: GlobalSize;
  className?: string;
  hasError?: boolean;
  /** Label for an extra "no selection" row at the top of the list. */
  unselectedOptionLabel?: string;
}

/**
 * Dropdown for ISO 3166-1 alpha-2 country codes. Options come from
 * `ReferenceDataProvider` (GraphQL-fed in apps, fixture-fed in Storybook), with
 * preferred countries bubbling to the top.
 */
export function CountrySelector({
  id,
  testId,
  value,
  onChange,
  onBlur,
  disabled,
  autoFocus,
  defaultOpen,
  isSearchable = false,
  height,
  className,
  hasError,
  unselectedOptionLabel
}: CountrySelectorProps) {
  const countries = useCountries();
  const options = useMemo(
    () =>
      countries.map(c => ({
        value: c.code,
        label: `${c.code} — ${c.name}`,
        preferred: isPreferredCountry(c.code)
      })),
    [countries]
  );

  const current = value == null ? '' : String(value).toUpperCase();

  return (
    <Select
      id={id}
      testId={testId}
      isSearchable={isSearchable}
      defaultOpen={defaultOpen}
      options={options}
      value={current}
      UnselectedOption={unselectedOptionLabel}
      onChange={next => {
        const picked = Array.isArray(next) ? next[0] : next;
        onChange(picked == null ? '' : String(picked).toUpperCase());
      }}
      onBlur={onBlur}
      autoFocus={autoFocus}
      disabled={disabled}
      height={height}
      className={className}
      hasError={hasError}
    />
  );
}

CountrySelector.displayName = 'CountrySelector';
